import React, { useRef, forwardRef, useImperativeHandle, useEffect, useState, useMemo } from 'react';
import { CircularRange, CircularRangeProps, CircularRangeRef } from '../../../components/CircularRange';
import { Box } from '@components/Box/Box';
import styles from "../styles.module.scss";
import classnames from "classnames";

/**
 * Represents a speed threshold with associated visual properties
 */
export interface SpeedStop {
  speed: number;
  color: string;
  supplementalText?: string;
  supplementalTextColor?: string;
}

/**
 * Props for the TicketCalculatorSpeedometer component
 * Extends CircularRangeProps with additional speedometer-specific properties
 */
export interface TicketCalculatorSpeedometerProps extends CircularRangeProps {
  speedStops?: Array<SpeedStop>;
}

/**
 * Ref interface for controlling the speedometer externally
 * Provides methods to get and set the current speed value
 */
export interface TicketCalculatorSpeedometerRef {
  setValue: (value: number) => void;
  getValue: () => number;
  resetUserInteraction: () => void;
  setValueWithoutInteraction: (value: number) => void;
  hasUserInteracted: () => boolean;
}

/**
 * TicketCalculatorSpeedometer Component
 * 
 * A circular speedometer component that displays speed with color-coded zones
 * and provides interactive speed selection with visual feedback.
 * 
 * Features:
 * - Color-coded speed zones (green, yellow, orange, red)
 * - Dynamic gradient based on speed stops
 * - Supplemental text display for speed violations
 * - Rounded integer values only
 * - External control via ref methods
 */
const TicketCalculatorSpeedometer = forwardRef<TicketCalculatorSpeedometerRef, TicketCalculatorSpeedometerProps>(({
  activeLabel = "80",
  className = "speedometer",
  enableMin = true,
  end = 520,
  haptic = "50,130",
  indices = 90,
  labels = "0:0,50:50,80:80,90:90,130:130,200:200",
  max = 200,
  min = 0,
  shiftStep = 10,
  start = 200,
  suffix = "Km/t",
  value = 80,
  defaultText = "Drej for at vælge hastighed",
  speedStops = [
    { speed: 0, color: "#33FF00", supplementalText: "Ingen fartoverskridelse", supplementalTextColor: "#000" },   // Bright green
    { speed: 25, color: "#66FF00" },   // Green-yellow
    { speed: 50, color: "#F2C94C", supplementalText: "Mindre fartoverskridelse", supplementalTextColor: "#000" },  // Yellow
    { speed: 65, color: "#FF8C00" },  // Orange
    { speed: 80, color: "#EB5757", supplementalText: "Kraftig fartoverskridelse", supplementalTextColor: "#fff" },  // Red
    { speed: 200, color: "#EB5757" }   // Red
  ],
  onChange,
  onInput
}, ref) => {
  const circularRangeRef = useRef<CircularRangeRef>(null);
  const [speedometerRef, setSpeedometerRef] = useState<HTMLDivElement | null>(null);
  const [isSpeedometerVisible, setIsSpeedometerVisible] = useState(false);
  const [speedometerValue, setSpeedometerValue] = useState(Number(value.toFixed(0)));
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  /**
   * Handles speedometer value changes and ensures values are always rounded to integers
   * @param {number} newValue - The new speed value to set
   */
  const handleSpeedometerChange = (newValue: number) => {
    const roundedValue = Number(newValue.toFixed(0));
    setSpeedometerValue(roundedValue);
    setHasUserInteracted(true);
  };

  const [speedometerColors, setSpeedometerColors] = useState<Record<string, string>>({
    // Background
    bg: "rgba(0, 0, 0, 0.2)",
    bgMask: "linear-gradient(to bottom, #000 50%, #0000 85%)",
    bgScale: "1.1",

    // Fill colors
    fill: "#33FF00",
    fillStart: "#33FF00",
    fillMiddle: "#F2C94C",
    fillEnd: "#EB5757",
    fillGradient: ` conic-gradient(from calc(var(--_start) * 1deg),
			#33FF00 0deg,
			#66FF00 35deg,
			#F2C94C 70deg,
			#FF8C00 91deg,
			#EB5757 112deg,
			#EB5757 360deg)`,

    // Indices
    indiceBdrs: "0",
    indiceC: "rgba(255, 255, 255, 0.2)",
    indiceH: "7px",
    indiceW: "2px",
    indicesW: "83%",

    // Labels
    labelsC: "rgba(255, 255, 255, 0.35)",
    labelsFf: "DM Sans, Arial, sans-serif",
    labelsFs: "12px",
    labelsFw: "300",
    labelsW: "73%",
    activeLabelC: "#fff",

    // Output/Value display
    outputAs: "end",
    outputC: "#fff",
    outputH: "400px",
    outputFf: "DM Sans, Arial, sans-serif",
    outputFs: "5rem",
    outputFw: "100",
    outputGr: "3",
    outputSuffixFf: "Safety_Regular, Helvetica Neue, Helvetica, Arial, sans-serif",
    outputSuffixFs: "24px",
    outputSuffixFw: "400",
    outputSupplementalFf: "DM Sans, Arial, sans-serif",
    outputSupplementalFs: "12px",
    outputSupplementalFw: "400",
    outputSupplementalBgC: "#33FF00",
    outputSupplementalTextC: "#000",
    rows: "7",

    // Thumb
    thumb: "#33FF00",
    thumbMin: "#33FF00",
    thumbBxsh: "0 0 0 2px Canvas",
    thumbBxshFocus: "0 0 0 2px Canvas",

    // Track
    track: "rgba(255, 255, 255, 0.2)",
    trackStart: "rgba(255, 255, 255, 0.5)",
    trackEnd: "rgba(255, 255, 255, 0.5)",
    trackSz: "18px",
  });

  /**
   * Exposes methods to control the speedometer externally via ref
   * Provides setValue and getValue methods for parent component control
   */
  useImperativeHandle(ref, () => ({
    /**
     * Sets the speedometer value and updates both the circular range and internal state
     * @param {number} newValue - The new speed value to set
     */
    setValue: (newValue: number) => {
      const roundedValue = Number(newValue.toFixed(0));
      circularRangeRef.current?.setValue(roundedValue);
      setSpeedometerValue(roundedValue);
      setHasUserInteracted(true);
    },
    /**
     * Gets the current speedometer value
     * @returns {number} The current rounded speed value
     */
    getValue: () => {
      return speedometerValue;
    },
    resetUserInteraction: () => {
      setHasUserInteracted(false);
    },
    setValueWithoutInteraction: (newValue: number) => {
      const roundedValue = Number(newValue.toFixed(0));
      circularRangeRef.current?.setValue(roundedValue);
      setSpeedometerValue(roundedValue);
      // Don't set hasUserInteracted to true
    },
    hasUserInteracted: () => {
      return hasUserInteracted;
    }
  }));

  /**
   * Applies CSS custom properties to the speedometer element for styling
   * Updates colors, fonts, and visual properties based on speedometerColors state
   */
  useEffect(() => {
    if (speedometerRef) {
      const properties = {
        // Background
        '--circular-range-bg': speedometerColors.bg,
        '--circular-range-bg-mask': speedometerColors.bgMask,
        '--circular-range-bg-scale': speedometerColors.bgScale,

        // Fill colors
        '--circular-range-fill': speedometerColors.fill,
        '--circular-range-fill-end': speedometerColors.fillEnd,
        '--circular-range-fill-middle': speedometerColors.fillMiddle,
        '--circular-range-fill-start': speedometerColors.fillStart,
        '--circular-range-fill-gradient': speedometerColors.fillGradient,

        // Indices
        '--circular-range-indice-bdrs': speedometerColors.indiceBdrs,
        '--circular-range-indice-c': speedometerColors.indiceC,
        '--circular-range-indice-h': speedometerColors.indiceH,
        '--circular-range-indice-w': speedometerColors.indiceW,
        '--circular-range-indices-w': speedometerColors.indicesW,

        // Labels
        '--circular-range-labels-c': speedometerColors.labelsC,
        '--circular-range-labels-ff': speedometerColors.labelsFf,
        '--circular-range-labels-fw': speedometerColors.labelsFw,
        '--circular-range-labels-fs': speedometerColors.labelsFs,
        '--circular-range-labels-w': speedometerColors.labelsW,
        '--circular-range-active-label-c': speedometerColors.activeLabelC,

        // Output/Value display
        '--circular-range-output-as': speedometerColors.outputAs,
        '--circular-range-output-c': speedometerColors.outputC,
        '--circular-range-output-h': speedometerColors.outputH,
        '--circular-range-output-ff': speedometerColors.outputFf,
        '--circular-range-output-fs': speedometerColors.outputFs,
        '--circular-range-output-fw': speedometerColors.outputFw,
        '--circular-range-output-gr': speedometerColors.outputGr,
        '--circular-range-output-suffix-ff': speedometerColors.outputSuffixFf,
        '--circular-range-output-suffix-fs': speedometerColors.outputSuffixFs,
        '--circular-range-output-suffix-fw': speedometerColors.outputSuffixFw,
        '--circular-range-output-supplemental-ff': speedometerColors.outputSupplementalFf,
        '--circular-range-output-supplemental-fs': speedometerColors.outputSupplementalFs,
        '--circular-range-output-supplemental-fw': speedometerColors.outputSupplementalFw,
        '--circular-range-output-supplemental-bg-c': speedometerColors.outputSupplementalBgC,
        '--circular-range-output-supplemental-text-c': speedometerColors.outputSupplementalTextC,
        '--circular-range-rows': speedometerColors.rows,

        // Thumb
        '--circular-range-thumb': speedometerColors.thumb,
        '--circular-range-thumb-min': speedometerColors.thumbMin,
        '--circular-range-thumb-bxsh': speedometerColors.thumbBxsh,
        '--circular-range-thumb-bxsh-focus': speedometerColors.thumbBxshFocus,

        // Track
        '--circular-range-track': speedometerColors.track,
        '--circular-range-track-start': speedometerColors.trackStart,
        '--circular-range-track-end': speedometerColors.trackEnd,
        '--circular-range-track-sz': speedometerColors.trackSz,
      };

      Object.entries(properties).forEach(([property, value]) => {
        speedometerRef.style.setProperty(property, value);
      });

      setIsSpeedometerVisible(true);
    }
  }, [speedometerRef, speedometerColors]);

  /**
   * Calculates the appropriate color for the current speed position
   * Uses linear interpolation between speed stops to determine color
   * @param {number} value - The speed value (unused, uses speedometerValue instead)
   * @returns {string} The hex color code for the current speed
   */
  const getGradientColorAtPosition = (value: number): string => {
    // Use the rounded speedometerValue directly since it's already rounded
    const speed = speedometerValue;

    // Find the appropriate color range
    for (let i = 0; i < speedStops.length - 1; i++) {
      const currentStop = speedStops[i];
      const nextStop = speedStops[i + 1];

      if (speed >= currentStop.speed && speed <= nextStop.speed) {
        // Calculate interpolation factor
        const range = nextStop.speed - currentStop.speed;
        const factor = range > 0 ? (speed - currentStop.speed) / range : 0;

        // Simple linear interpolation between colors
        const color1 = currentStop.color;
        const color2 = nextStop.color;

        // Convert hex to RGB
        const hexToRgb = (hex: string) => {
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          } : null;
        };

        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        if (rgb1 && rgb2) {
          const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * factor);
          const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * factor);
          const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * factor);

          const result = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
          return result;
        }

        return color1;
      }
    }

    // Fallback to red for speeds above the last stop
    return "#EB5757";
  };

  /**
   * Calculates the current speed stop for supplemental text display
   * Finds the appropriate speed stop based on the current speedometer value
   * @returns {SpeedStop | undefined} The current speed stop with supplemental text
   */
  const currentSpeedStop = useMemo(() => {
    const speeds = speedStops.filter(stop => speedometerValue >= stop.speed && stop.supplementalText);
    return speeds[speeds.length - 1];
  }, [speedometerValue, speedStops]);

  /**
   * Updates colors and calls callbacks when speedometer value changes
   * Calculates new thumb color and updates all visual properties
   */
  useEffect(() => {
    onChange?.(speedometerValue);
    onInput?.(speedometerValue);

    // Calculate new thumb color using the rounded speedometerValue
    const newThumbColor = getGradientColorAtPosition(speedometerValue);

    // Update track start color
    const newTrackStartColor = speedometerValue === 0
      ? "rgba(255,255,255,0.3)"
      : speedStops[0].color;

    // Update all colors at once to avoid multiple re-renders
    setSpeedometerColors(prevColors => ({
      ...prevColors,
      thumb: newThumbColor,
      trackStart: newTrackStartColor,
      outputSupplementalBgC: newThumbColor,
      outputSupplementalTextC: currentSpeedStop.supplementalTextColor || "#000",
    }));
  }, [speedometerValue, onChange, onInput, speedometerRef, max, speedStops]);

  /**
   * Updates the gradient with proper color stops based on speed ranges
   * Calculates angles for each speed stop and creates a conic gradient
   */
  const updateGradient = () => {
    if (speedometerRef) {
      // Calculate angles based on speed ranges over the total angle
      const totalAngle = end - start; // Dynamic total angle based on props
      const speedRange = max - min; // Dynamic speed range based on props

      let gradientStops = `conic-gradient(from calc(var(--_start) * 1deg)`;

      speedStops.forEach((stop) => {
        // Calculate the percentage of this speed within the min-max range
        const speedPercentage = speedRange > 0 ? (stop.speed - min) / speedRange : 0;
        const angle = speedPercentage * totalAngle;
        gradientStops += `, ${stop.color} ${angle}deg`;
      });

      gradientStops += `, ${speedStops[speedStops.length - 1].color} 360deg)`;
      setSpeedometerColors(prevColors => ({
        ...prevColors,
        fillGradient: gradientStops,
        trackStart: speedStops[0].color,
      }));
    }
  };

  /**
   * Initializes gradient on component mount and updates when speed stops change
   * Recalculates the gradient whenever max speed or speed stops are modified
   */
  useEffect(() => {
    updateGradient();
  }, [max, speedStops]);

  /**
   * Updates the speedometer value when the value prop changes
   * Only updates if user hasn't interacted with the speedometer
   */
  useEffect(() => {
    if (!hasUserInteracted) {
      setSpeedometerValue(Number(value.toFixed(0)));
    }
  }, [value, hasUserInteracted]);

  return (
    <Box className={classnames(styles.ticketcalculatorSpeedometer, isSpeedometerVisible ? "" : "visuallyhidden")}>
      <CircularRange
        ref={circularRangeRef}
        activeLabel={activeLabel}
        className={className}
        enableMin={enableMin}
        end={end}
        haptic={haptic}
        indices={indices}
        labels={labels}
        max={max}
        min={min}
        shiftStep={shiftStep}
        start={start}
        suffix={suffix}
        value={speedometerValue}
        defaultText={defaultText}
        supplementalText={currentSpeedStop.supplementalText}
        onChange={handleSpeedometerChange}
        onInput={handleSpeedometerChange}
        setElementRef={setSpeedometerRef}
      />
    </Box>
  );
});

TicketCalculatorSpeedometer.displayName = 'TicketCalculatorSpeedometer';

export default TicketCalculatorSpeedometer;